import { ExpandedLinkElement, MentionElement } from '@all-hands-hq/plex-graphql-schema';
import React, { useContext } from 'react';

import { LinkPreviewMetadataFragment, MentionsPlexRegisteredUserFragment } from '../../../graphql/gen/types';

export type Entities = {
  expanded_link: LinkPreviewMetadataFragment[];
  mention: MentionsPlexRegisteredUserFragment[];
};

type EntityType<Element extends ExpandedLinkElement | MentionElement> = Entities[Element['type']][0];

// Overloaded function definition used to clarify the external type
export function getEntity<Element extends ExpandedLinkElement | MentionElement>(
  entities: Entities,
  element: Element
): EntityType<Element>;
export function getEntity(
  entities: Entities,
  element: ExpandedLinkElement | MentionElement
): EntityType<typeof element> {
  switch (element.type) {
    case 'mention': {
      return getMentionEntity(entities, element);
    }
    case 'expanded_link': {
      return getLinkEntity(entities, element);
    }
  }
}

export const EntitiesContext = React.createContext<Entities | undefined>(undefined);

export const useEntitiesContext = (): Entities => {
  const entities = useContext(EntitiesContext);

  if (entities == null) {
    throw new Error(
      `No "EntitiesContext" has been provided. Ensure your Slate editor is wrapped with an EntitiesContext.Provider`
    );
  }

  return entities;
};

const getMentionEntity = (entities: Entities, element: MentionElement): MentionsPlexRegisteredUserFragment => {
  const mention = entities.mention.find((mention) => mention.email === element.email);

  if (mention == null) {
    throw new Error(`Invalid mention element ${JSON.stringify(element)}`);
  }

  return mention;
};

const getLinkEntity = (entities: Entities, element: ExpandedLinkElement): LinkPreviewMetadataFragment => {
  const link = entities.expanded_link.find((link) => link.id === element.url);

  if (link == null) {
    throw new Error(`Invalid expanded_link element ${JSON.stringify(element)}`);
  }

  return link;
};
